import { useEffect, useState } from "react";
import { toast } from "sonner";
import {
    Loader2, Plus, Trash2, GripVertical, Layers, ShieldCheck, Check, X,
} from "lucide-react";
import {
    DndContext, closestCenter, KeyboardSensor, PointerSensor,
    useSensor, useSensors,
} from "@dnd-kit/core";
import {
    arrayMove, SortableContext, sortableKeyboardCoordinates,
    useSortable, verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import {
    listStagesManage, createStage, updateStageMeta,
    deleteStage as apiDeleteStage, reorderStages,
    listStagesMetrics,
} from "../../lib/crmApi";
import { adminMe, formatApiError } from "../../lib/adminApi";

// Curated palette — 12 colors that pair well and cover the typical CRM
// funnel (cold → warm → hot → won/lost). Free-form hex is also accepted.
const PALETTE = [
    "#64748B", "#0EA5E9", "#06B6D4", "#14B8A6",
    "#10B981", "#059669", "#F59E0B", "#F97316",
    "#E11D48", "#EC4899", "#8B5CF6", "#6366F1",
];

const fmtDwell = (h) => {
    if (!h || h < 1) return "<1t";
    if (h < 24) return `${Math.round(h)}t`;
    return `${(h / 24).toFixed(1)}d`;
};

/**
 * Full pipeline stage management — visible ONLY to super_admin. Sales reps
 * still see stages via /crm/stages but cannot edit here.
 */
export default function StagesSection() {
    const [me, setMe] = useState(null);
    const [stages, setStages] = useState([]);
    const [metrics, setMetrics] = useState({});
    const [loading, setLoading] = useState(true);
    const [newName, setNewName] = useState("");
    const [newStops, setNewStops] = useState(false);
    const [newColor, setNewColor] = useState(PALETTE[1]);
    const [creating, setCreating] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editName, setEditName] = useState("");
    const [colorPickerFor, setColorPickerFor] = useState(null);

    const sensors = useSensors(
        useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
        useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates }),
    );

    const load = async () => {
        setLoading(true);
        try {
            const meData = await adminMe();
            setMe(meData);
            if (meData.role === "super_admin") {
                const [s, m] = await Promise.all([listStagesManage(), listStagesMetrics()]);
                setStages(s);
                setMetrics(Object.fromEntries(m.map((row) => [row.id, row])));
            }
        } catch (e) {
            console.warn(e);
        } finally { setLoading(false); }
    };

    useEffect(() => { load(); }, []);

    if (loading) return null;
    if (!me || me.role !== "super_admin") return null;

    const handleDragEnd = async (event) => {
        const { active, over } = event;
        if (!over || active.id === over.id) return;
        const oldIdx = stages.findIndex((s) => s.id === active.id);
        const newIdx = stages.findIndex((s) => s.id === over.id);
        const next = arrayMove(stages, oldIdx, newIdx);
        setStages(next);
        try {
            await reorderStages(next.map((s) => s.id));
        } catch (err) {
            toast.error(formatApiError(err));
            setStages(await listStagesManage());
        }
    };

    const submitCreate = async (e) => {
        e.preventDefault();
        if (!newName.trim()) return;
        setCreating(true);
        try {
            await createStage({ name: newName.trim(), stops_automation: newStops, color: newColor });
            toast.success("Stage oprettet");
            setNewName(""); setNewStops(false); setNewColor(PALETTE[1]);
            setStages(await listStagesManage());
        } catch (err) {
            toast.error(formatApiError(err));
        } finally { setCreating(false); }
    };

    const setColor = async (stage, color) => {
        try {
            await updateStageMeta(stage.id, { color });
            setStages(await listStagesManage());
            setColorPickerFor(null);
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const saveRename = async (stage) => {
        if (!editName.trim() || editName === stage.name) {
            setEditingId(null); return;
        }
        try {
            await updateStageMeta(stage.id, { name: editName.trim() });
            toast.success("Stage omdøbt — leads og flow opdateret");
            setEditingId(null);
            setStages(await listStagesManage());
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const toggleStops = async (stage) => {
        try {
            await updateStageMeta(stage.id, { stops_automation: !stage.stops_automation });
            setStages(await listStagesManage());
        } catch (err) { toast.error(formatApiError(err)); }
    };

    const remove = async (stage) => {
        if (!window.confirm(`Slet stagen "${stage.name}"?`)) return;
        try {
            await apiDeleteStage(stage.id);
            toast.success("Stage slettet");
            setStages(await listStagesManage());
        } catch (err) { toast.error(formatApiError(err)); }
    };

    return (
        <div className="bg-white rounded-xl border border-slate-200 p-6 space-y-4" data-testid="crm-settings-stages">
            <div className="flex items-center gap-2">
                <Layers className="h-5 w-5 text-sky-600" />
                <h2 className="text-lg font-semibold text-slate-900">Pipeline-stages</h2>
            </div>
            <p className="text-sm text-slate-600">
                Byg din egen pipeline. Træk for at ændre rækkefølge, klik navnet for at omdøbe.
                System-stages (låst med skjold) kan omdøbes men ikke slettes — de bruges automatisk
                af "Vundet"/"Tabt"/"Møde booket" knapperne.
            </p>

            <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
                <SortableContext items={stages.map((s) => s.id)} strategy={verticalListSortingStrategy}>
                    <div className="rounded-lg border border-slate-200 divide-y divide-slate-100">
                        {stages.map((stage) => (
                            <StageRow key={stage.id} stage={stage}
                                metrics={metrics[stage.id]}
                                colorPickerOpen={colorPickerFor === stage.id}
                                onOpenColorPicker={() => setColorPickerFor(stage.id === colorPickerFor ? null : stage.id)}
                                onPickColor={(c) => setColor(stage, c)}
                                editing={editingId === stage.id} editName={editName}
                                onStartEdit={() => { setEditingId(stage.id); setEditName(stage.name); }}
                                onCancelEdit={() => setEditingId(null)}
                                onChangeEdit={setEditName}
                                onSaveRename={() => saveRename(stage)}
                                onToggleStops={() => toggleStops(stage)}
                                onDelete={() => remove(stage)}
                            />
                        ))}
                    </div>
                </SortableContext>
            </DndContext>

            <form onSubmit={submitCreate} className="pt-3 border-t border-slate-200 grid grid-cols-1 md:grid-cols-4 gap-3 items-center">
                <input value={newName} onChange={(e) => setNewName(e.target.value)}
                    placeholder="Nyt stage-navn (fx 'Interesseret')"
                    data-testid="new-stage-name" maxLength={60}
                    className="md:col-span-2 rounded-lg border border-slate-300 h-10 px-3 text-sm" />
                <div className="flex items-center gap-2">
                    {PALETTE.slice(0, 6).map((c) => (
                        <button key={c} type="button" onClick={() => setNewColor(c)}
                            title={c}
                            className={`h-6 w-6 rounded-full border-2 transition-transform ${newColor === c ? "border-slate-800 scale-110" : "border-white"}`}
                            style={{ backgroundColor: c }} />
                    ))}
                    <input type="color" value={newColor} onChange={(e) => setNewColor(e.target.value)}
                        title="Vælg brugerdefineret farve"
                        className="h-6 w-8 rounded border border-slate-200 cursor-pointer" />
                </div>
                <label className="inline-flex items-center gap-2 text-sm text-slate-700 md:col-span-1">
                    <input type="checkbox" checked={newStops} onChange={(e) => setNewStops(e.target.checked)} />
                    Stopper automation
                </label>
                <button type="submit" disabled={creating || !newName.trim()}
                    data-testid="create-stage-btn"
                    className="md:col-span-4 inline-flex items-center gap-2 rounded-lg bg-sky-600 hover:bg-sky-700 text-white text-sm font-semibold px-4 h-10 disabled:opacity-60 justify-center">
                    {creating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Plus className="h-4 w-4" />} Tilføj stage
                </button>
            </form>
        </div>
    );
}

function StageRow({ stage, metrics, colorPickerOpen, onOpenColorPicker, onPickColor,
    editing, editName, onStartEdit, onCancelEdit, onChangeEdit,
    onSaveRename, onToggleStops, onDelete }) {
    const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: stage.id });
    const style = {
        transform: CSS.Transform.toString(transform),
        transition,
        opacity: isDragging ? 0.5 : 1,
    };
    const canDelete = !stage.is_system && stage.lead_count === 0;
    const deleteTitle = stage.is_system
        ? "System-stage kan ikke slettes"
        : stage.lead_count > 0
            ? `${stage.lead_count} lead${stage.lead_count === 1 ? "" : "s"} i denne stage — flyt dem først`
            : "Slet";
    const stageColor = stage.color || "#64748B";

    return (
        <div ref={setNodeRef} style={style} data-testid={`stage-row-${stage.name}`}
             className="relative flex items-center gap-3 p-3 bg-white">
            <button {...attributes} {...listeners} className="cursor-grab active:cursor-grabbing text-slate-400 hover:text-slate-600" title="Træk for at flytte">
                <GripVertical className="h-4 w-4" />
            </button>
            {/* Color swatch — click opens palette */}
            <button type="button" onClick={onOpenColorPicker}
                data-testid={`stage-color-${stage.name}`}
                title="Skift farve"
                className="h-8 w-8 rounded-lg border-2 border-white shadow-sm ring-1 ring-slate-200 hover:ring-slate-400 flex-shrink-0"
                style={{ backgroundColor: stageColor }} />
            {colorPickerOpen && (
                <div className="absolute left-14 top-14 z-20 bg-white rounded-xl border border-slate-200 shadow-lg p-3 grid grid-cols-6 gap-2"
                     data-testid={`stage-color-picker-${stage.name}`}>
                    {PALETTE.map((c) => (
                        <button key={c} type="button" onClick={() => onPickColor(c)}
                            className={`h-7 w-7 rounded-lg border-2 transition-transform hover:scale-110 ${stageColor === c ? "border-slate-800" : "border-white ring-1 ring-slate-200"}`}
                            style={{ backgroundColor: c }} title={c} />
                    ))}
                    <input type="color" value={stageColor}
                        onChange={(e) => onPickColor(e.target.value)}
                        className="col-span-6 h-8 w-full rounded border border-slate-200 cursor-pointer" />
                </div>
            )}
            <div className="flex-1 min-w-0">
                {editing ? (
                    <input value={editName} onChange={(e) => onChangeEdit(e.target.value)}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") onSaveRename();
                            if (e.key === "Escape") onCancelEdit();
                        }}
                        autoFocus maxLength={60}
                        data-testid={`stage-edit-input-${stage.name}`}
                        className="w-full rounded-lg border border-sky-400 h-8 px-2 text-sm font-semibold text-slate-900" />
                ) : (
                    <button onClick={onStartEdit} className="text-sm font-semibold text-slate-900 hover:text-sky-700 truncate text-left"
                        data-testid={`stage-name-${stage.name}`}>
                        {stage.name}
                    </button>
                )}
                <div className="flex items-center gap-1.5 mt-0.5 flex-wrap">
                    {stage.is_system && (
                        <span className="inline-flex items-center gap-1 rounded-full bg-slate-100 text-slate-700 text-[10px] font-semibold px-2 py-0.5">
                            <ShieldCheck className="h-3 w-3" /> System
                        </span>
                    )}
                    {stage.is_default && (
                        <span className="inline-flex items-center rounded-full bg-emerald-100 text-emerald-800 text-[10px] font-semibold px-2 py-0.5">
                            Default for nye leads
                        </span>
                    )}
                    <span className="text-[10px] text-slate-500">
                        {stage.lead_count} lead{stage.lead_count === 1 ? "" : "s"}
                    </span>
                </div>
                {/* Metrics line */}
                {metrics && (metrics.lifetime_count > 0 || metrics.current_count > 0) && (
                    <div className="mt-1 flex flex-wrap items-center gap-x-3 gap-y-0.5 text-[11px] text-slate-500" data-testid={`stage-metrics-${stage.name}`}>
                        <span title="Gennemsnitlig tid som lead er i denne stage">
                            ⌀ {fmtDwell(metrics.avg_hours_in_stage)}
                        </span>
                        <span title="Hvor mange leads der nogensinde er passeret gennem denne stage — bortset fra system-stages som normalt er slut-tilstande">
                            {metrics.lifetime_count} passeret
                        </span>
                        {metrics.lifetime_count > 0 && stage.system_key !== "won" && stage.system_key !== "lost" && (
                            <span title="Andel af leads der har været i denne stage OG endte som Vundet"
                                  className={metrics.conversion_rate >= 0.25 ? "text-emerald-600 font-semibold" : ""}>
                                {(metrics.conversion_rate * 100).toFixed(0)}% win-rate
                            </span>
                        )}
                    </div>
                )}
            </div>
            <label className="inline-flex items-center gap-1.5 text-xs text-slate-600" title="Stopper email-automation når leads flyttes hertil">
                <input type="checkbox" checked={stage.stops_automation} onChange={onToggleStops}
                    data-testid={`stage-stops-${stage.name}`} />
                <span className="hidden sm:inline">Stopper</span>
            </label>
            {editing ? (
                <div className="flex items-center gap-1">
                    <button onClick={onSaveRename} className="p-1.5 rounded text-emerald-600 hover:bg-emerald-50"
                        title="Gem" data-testid={`stage-save-${stage.name}`}><Check className="h-4 w-4" /></button>
                    <button onClick={onCancelEdit} className="p-1.5 rounded text-slate-500 hover:bg-slate-100"
                        title="Annullér"><X className="h-4 w-4" /></button>
                </div>
            ) : (
                <button onClick={onDelete} disabled={!canDelete}
                    title={deleteTitle}
                    data-testid={`stage-delete-${stage.name}`}
                    className={`p-1.5 rounded ${canDelete ? "text-rose-600 hover:bg-rose-50" : "text-slate-300 cursor-not-allowed"}`}>
                    <Trash2 className="h-4 w-4" />
                </button>
            )}
        </div>
    );
}
